import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Sun, Moon, User, LogOut, ChevronDown, Menu, X } from 'lucide-react';

const Header = ({ onMenuClick, sidebarOpen, pageTitle, pageDescription }) => {
  const { theme, isDark, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('[data-user-menu]')) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setShowUserMenu(false);
  };

  const iconBtn = {
    width: '38px',
    height: '38px',
    borderRadius: '10px',
    backgroundColor: theme.colors.backgroundSecondary,
    border: `1px solid ${theme.colors.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    color: theme.colors.text,
    flexShrink: 0,
  };

  return (
    <header style={{
      backgroundColor: theme.colors.headerBg,
      borderBottom: `1px solid ${theme.colors.border}`,
      height: '60px',
      display: 'flex',
      alignItems: 'center',
      padding: '0 16px',
      position: 'sticky',
      top: 0,
      zIndex: 200,
      boxShadow: theme.shadows.sm,
      gap: '12px',
    }}>
      {/* Mobile hamburger */}
      {onMenuClick && (
        <button
          className="show-mobile"
          style={{ ...iconBtn, display: 'none' }}
          onClick={onMenuClick}
          aria-label="Toggle navigation"
        >
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      )}

      {/* Page Title & Description */}
      {pageTitle && (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
          <div style={{ fontSize: '15px', fontWeight: '600', color: theme.colors.text }}>
            {pageTitle}
          </div>
          {pageDescription && (
            <div style={{ fontSize: '12px', color: theme.colors.textSecondary, marginTop: '2px' }}>
              {pageDescription}
            </div>
          )}
        </div>
      )}

      <div style={{ flex: 1 }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* Theme toggle */}
        <button
          style={iconBtn}
          onClick={toggleTheme}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary; e.currentTarget.style.borderColor = theme.colors.primary; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = theme.colors.backgroundSecondary; e.currentTarget.style.borderColor = theme.colors.border; }}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? <Sun size={17} /> : <Moon size={17} />}
        </button>

        {/* User menu */}
        {user && (
          <div style={{ position: 'relative' }} data-user-menu>
            <button
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '5px 10px', borderRadius: '10px',
                backgroundColor: 'transparent',
                border: `1px solid ${theme.colors.border}`,
                cursor: 'pointer', transition: 'all 0.2s',
              }}
              onClick={() => setShowUserMenu(!showUserMenu)}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = theme.colors.backgroundSecondary; e.currentTarget.style.borderColor = theme.colors.primary; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = theme.colors.border; }}
            >
              <div style={{
                width: '30px', height: '30px', borderRadius: '50%',
                background: `linear-gradient(135deg, ${theme.colors.primary}30, ${theme.colors.primary}15)`,
                border: `1.5px solid ${theme.colors.primary}40`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: theme.colors.primary,
              }}>
                <User size={15} />
              </div>
              <div className="hide-mobile" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1px' }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: theme.colors.text, lineHeight: '1', whiteSpace: 'nowrap' }}>{user.name}</span>
                <span style={{ fontSize: '10px', color: theme.colors.textSecondary, textTransform: 'capitalize', lineHeight: '1' }}>{user.role?.toLowerCase()}</span>
              </div>
              <ChevronDown size={13} color={theme.colors.textTertiary} />
            </button>

            {showUserMenu && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: '8px',
                backgroundColor: theme.colors.cardBg,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '10px', boxShadow: theme.shadows.lg,
                minWidth: '180px', overflow: 'hidden', zIndex: 1000,
              }}>
                <div style={{ padding: '12px 16px', borderBottom: `1px solid ${theme.colors.border}` }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: theme.colors.text }}>{user.name}</div>
                  <div style={{ fontSize: '11px', color: theme.colors.textSecondary, marginTop: '2px' }}>{user.email}</div>
                </div>
                <button
                  style={{
                    padding: '11px 16px', display: 'flex', alignItems: 'center', gap: '10px',
                    cursor: 'pointer', backgroundColor: 'transparent', border: 'none', width: '100%',
                    textAlign: 'left', fontSize: '14px', color: theme.colors.error, transition: 'background-color 0.2s',
                  }}
                  onClick={handleLogout}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.backgroundSecondary}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <LogOut size={15} />
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
